/**
 * Tenant Resolution Utilities
 * Extracts tenant from the page's host name the same way the server does.
 *
 * The server's patterns (App:DomainResolver, e.g. "{0}.mot-r.app,{0}-motr.initto.work") arrive in
 * window.__MOTR_CONFIG__ from /api/app-config, loaded by index.html before the app:
 *   acme.mot-r.app        -> tenant "acme"
 *   acme-motr.initto.work -> tenant "acme"
 * The built-in domain list below is only a fallback for when that script did not load.
 */

interface MotrRuntimeConfig {
  /** App:DomainResolver patterns, primary first; "{0}" is the tenant name. */
  domainPatterns?: string[];
}

declare global {
  interface Window {
    __MOTR_CONFIG__?: MotrRuntimeConfig;
  }
}

// Session storage key for impersonation (must match impersonation.ts)
const IMPERSONATED_TENANT_KEY = 'motr_impersonated_tenant_id';

// Base domains that indicate host (no tenant)
const HOST_DOMAINS = [
  'localhost',
  'mot-r.app',   // UAT API
  'mot-r.dev',   // UAT frontend
  'mot-r.co',    // Production API
  'mot-r.io',    // Production frontend
  'motr.com',
  'motrv2.com',  // Development
];

// Subdomains that should be treated as host
const HOST_SUBDOMAINS = [
  'www',
  'app',
  'api',
  'admin',
];

/**
 * Tenant name from the server's domain patterns, or undefined when no pattern matches (or the
 * patterns did not load), so the caller falls back to the built-in rules.
 */
const tenantFromServerPatterns = (hostname: string): string | null | undefined => {
  const host = hostname.toLowerCase();
  for (const pattern of window.__MOTR_CONFIG__?.domainPatterns ?? []) {
    const index = pattern.indexOf('{0}');
    if (index < 0) continue;

    const prefix = pattern.slice(0, index).toLowerCase();
    const suffix = pattern.slice(index + 3).toLowerCase();
    if (host.length <= prefix.length + suffix.length || !host.startsWith(prefix) || !host.endsWith(suffix)) {
      continue;
    }

    // One host label only: "a.b.mot-r.app" is not tenant "a.b".
    const name = host.slice(prefix.length, host.length - suffix.length);
    if (name.includes('.')) continue;

    return HOST_SUBDOMAINS.includes(name) ? null : name;
  }
  return undefined;
};

/**
 * Extract tenant name from the current URL's subdomain
 * @returns Tenant name or null if on host domain
 */
export const extractTenantFromUrl = (): string | null => {
  const hostname = window.location.hostname;

  const fromPatterns = tenantFromServerPatterns(hostname);
  if (fromPatterns !== undefined) {
    return fromPatterns;
  }

  // Check if it's a known host domain
  if (HOST_DOMAINS.some((domain) => hostname === domain || hostname.endsWith(`.${domain}`))) {
    // Extract potential tenant from subdomain
    const parts = hostname.split('.');

    // If there's a subdomain (e.g., tenant.mot-r.app)
    if (parts.length >= 3) {
      const subdomain = parts[0];

      // Skip if it's a reserved subdomain
      if (HOST_SUBDOMAINS.includes(subdomain.toLowerCase())) {
        return null;
      }

      return subdomain;
    }
  }

  // Local development - check for pattern like tenant.localhost
  if (hostname.includes('.localhost') || hostname.includes('.local')) {
    const parts = hostname.split('.');
    if (parts.length >= 2) {
      const subdomain = parts[0];
      if (!HOST_SUBDOMAINS.includes(subdomain.toLowerCase())) {
        return subdomain;
      }
    }
  }

  return null;
};

/**
 * Get tenant from URL query parameter (__tenant)
 * This is a fallback for environments where subdomain routing isn't possible
 */
export const extractTenantFromQuery = (): string | null => {
  const params = new URLSearchParams(window.location.search);
  return params.get('__tenant');
};

/**
 * Get tenant from localStorage
 * Used to persist tenant selection across page refreshes
 */
export const extractTenantFromStorage = (): string | null => {
  return localStorage.getItem('motr_tenant');
};

/**
 * Save tenant to localStorage
 */
export const saveTenantToStorage = (tenantName: string | null): void => {
  if (tenantName) {
    localStorage.setItem('motr_tenant', tenantName);
  } else {
    localStorage.removeItem('motr_tenant');
  }
};

/**
 * Get tenant ID from impersonation session
 * When impersonating a tenant, this takes priority over all other sources
 */
export const getImpersonatedTenantId = (): string | null => {
  return sessionStorage.getItem(IMPERSONATED_TENANT_KEY);
};

/**
 * Check if currently impersonating a tenant
 */
export const isImpersonating = (): boolean => {
  return sessionStorage.getItem('motr_impersonating') === 'true';
};

/**
 * Resolve tenant using priority:
 * 1. Impersonation (highest priority - from sessionStorage)
 * 2. URL subdomain
 * 3. Query parameter (__tenant)
 * 4. localStorage
 */
export const resolveTenant = (): string | null => {
  // Priority 1: Impersonation (highest priority)
  // When impersonating, always use the impersonated tenant ID
  if (isImpersonating()) {
    const impersonatedTenantId = getImpersonatedTenantId();
    if (impersonatedTenantId) {
      return impersonatedTenantId;
    }
  }

  // Priority 2: Subdomain
  const fromSubdomain = extractTenantFromUrl();
  if (fromSubdomain) {
    saveTenantToStorage(fromSubdomain);
    return fromSubdomain;
  }

  // Priority 3: Query parameter
  const fromQuery = extractTenantFromQuery();
  if (fromQuery) {
    saveTenantToStorage(fromQuery);
    return fromQuery;
  }

  // Priority 4: localStorage
  return extractTenantFromStorage();
};

/**
 * Build URL for a specific tenant
 */
export const buildTenantUrl = (
  tenantName: string,
  baseDomain = 'mot-r.app'
): string => {
  const protocol = window.location.protocol;
  const port = window.location.port ? `:${window.location.port}` : '';
  return `${protocol}//${tenantName}.${baseDomain}${port}`;
};
